import { useEffect, useState } from "react";
import { api, initials } from "../lib/api";
import { PhysicianDetailSheet } from "../components/PhysicianDetailSheet";
import { AddPhysicianDialog } from "../components/AddPhysicianDialog";
import { ImportCSVDialog } from "../components/ImportCSVDialog";
import { toast } from "sonner";
import { STAGES } from "../lib/constants";

const STAGES_DEPRECATED_LOCAL = [];

export default function Pipeline() {
  const [data, setData] = useState({});
  const [selected, setSelected] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const r = await api.get("/pipeline");
    setData(r.data);
  };

  useEffect(() => { load(); }, []);

  const onDrop = async (stage) => {
    if (!dragId) return;
    await api.put(`/physicians/${dragId}/stage`, { stage });
    toast.success(`Moved to ${stage}`);
    setDragId(null);
    load();
  };

  return (
    <div className="p-8 max-h-screen flex flex-col" data-testid="pipeline-page">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">Onboarding Pipeline</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Pipeline</h1>
          <p className="text-sm text-[#5C5E66] mt-2">Drag physicians between stages. Click any card to view details, notes &amp; cadence.</p>
        </div>
        <div className="flex gap-2">
          <ImportCSVDialog onImported={load} />
          <AddPhysicianDialog onAdded={load} />
        </div>
      </div>

      <div className="flex-1 overflow-x-auto kanban-scroll">
        <div className="flex gap-px bg-[#E2E2E5] border border-[#E2E2E5] min-w-max h-full">
          {STAGES.map((stage) => {
            const physicians = data[stage] || [];
            return (
              <div
                key={stage}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(stage)}
                className="bg-[#F7F7F8] w-72 flex flex-col"
                data-testid={`kanban-col-${stage.toLowerCase().replace(/\s+/g,"-")}`}
              >
                <div className="bg-white px-4 py-3 border-b border-[#E2E2E5] flex items-center justify-between sticky top-0">
                  <div className="text-xs font-mono uppercase tracking-wider font-semibold text-[#0A0B0F]">{stage}</div>
                  <div className="font-mono text-xs text-[#5C5E66] tabular-nums">{physicians.length}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
                  {physicians.length === 0 && <div className="text-center text-xs text-[#5C5E66] mt-6 font-mono uppercase tracking-wider">Empty</div>}
                  {physicians.map((p) => (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={() => setDragId(p.id)}
                      onClick={() => setSelected(p.id)}
                      data-testid={`kanban-card-${p.id}`}
                      className="bg-white border border-[#E2E2E5] p-3 cursor-pointer hover:border-[#006CFF] active:scale-95 transition-all"
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 bg-[#0A0B0F] text-white flex items-center justify-center font-mono font-semibold text-[10px] flex-shrink-0">{initials(p.full_name)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm leading-tight truncate">{p.full_name}</div>
                          <div className="text-[11px] text-[#5C5E66] truncate">{p.specialty || "—"}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-[#E2E2E5] flex items-center justify-between text-[10px] font-mono text-[#5C5E66]">
                        <span className="uppercase tracking-wider truncate">{p.practice_type}</span>
                        <span className="truncate ml-1">{p.city || "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PhysicianDetailSheet
        physicianId={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onUpdated={load}
      />
    </div>
  );
}
